/**
 * @module qbLinkage/handlers/conditionalFormat
 * @description QB 联动管道内同步 `conditional_format`（表格条件格式）
 *
 * 存储结构（与 ConditionalFormat 设置面板一致）：
 * - `table_bar_percentage`：条形图条件，每项含 `metric` / `table_code` / `id` 等
 * - `table_numerical_values`：数值条件，每项含 `metric` / `table_code` / `id`（QB 指标 id）/ `rules` 等
 *
 * 引用 QB 字段的方式：以 `table_code` + `column_name`（持久化在 `metric` 上）定位指标列；
 * 历史条形图数据可能将 `id` 与列名写为同一字符串，改名时需一并迁移。
 *
 * 联动策略：
 * - **prune**（metricRemoved / rawColumnRemoved）：删除仍指向已移除字段的配置行
 *   - 聚合模式：跟 `columnMetrics`（metricRemoved + rawColumnRemoved 经 metricLikeRemovedFields）
 *   - 明细模式：只跟 `columnRawColumns`（columnMetrics 与条件格式无关，见 apply 内分支）
 * - **migrate**（fieldIdentityRenamed，且非 displayOnly）：将 `metric`（及与旧列名相同的 `id`）映射为新列名
 *
 * 无变更时返回 `undefined`，避免向 pipeline 写入空 patch。
 */

import type { IQBLinkageHandler, IQBRenameEvent } from '../types';
import { hasNonEmptyRecordInLayers, metricLikeRemovedFields, patchBoth } from './shared';

/** 条件格式列表中的单行（条形图 / 数值条件共用） */
type TFormatRow = {
  /** 指标字段名，与 QB `column_name` 对齐 */
  metric?: string;
  /** 指标在 QB 中的稳定 id；旧数据可能等于 column_name */
  id?: string;
  table_code?: string;
};

/** prune 阶段：本次 QB 移除的字段身份 */
type TRemovedField = { column_name: string; table_code: string };

/** 需同步处理的两个子列表 key */
const CONDITIONAL_FORMAT_LIST_KEYS = [
  'table_bar_percentage',
  'table_numerical_values',
] as const;

/**
 * 判断配置行是否引用「本次已删除」的 QB 字段。
 *
 * 匹配规则与 columnPercentage 一致：`table_code` + `column_name`；
 * 行上无 `metric` 时回退用 `id`（兼容仅写了 id 的历史数据）。
 *
 * @param row - 条件格式配置行（条形图或数值条件列表中的一项）
 * @param removed - 本次 QB diff 检出的已移除字段列表（含 table_code、column_name）
 * @returns 若该行指向的字段已在 QB 中被删除，则为 `true`
 */
function isRowRemoved(row: TFormatRow, removed: TRemovedField[]): boolean {
  // 缺 table_code 时与面板默认一致，视为 t0
  const rowTable = row.table_code || 't0';
  // 持久化优先用 metric；历史条形图可能只写了 id 且 id === column_name
  const metricKey = row.metric ?? row.id;
  if (!metricKey) {
    // 无法关联到任何 QB 字段，保留该行（避免误删用户配置）
    return false;
  }
  // 同一 table 下 column_name 命中即视为需 prune
  return removed.some(
    r =>
      (r.table_code || 't0') === rowTable &&
      r.column_name === String(metricKey),
  );
}

/**
 * 从 conditional_format 中裁剪已失效的条形图 / 数值条件条目。
 *
 * @param cf - 单层配置（base 或 draft 上的 conditional_format 对象）
 * @param removed - 本次已移除的 QB 字段身份列表
 * @returns 有删行时返回浅拷贝后的新对象；无变更或入参为空时返回 `undefined`
 */
function pruneConditionalFormat(
  cf: Record<string, unknown> | undefined,
  removed: TRemovedField[],
): Record<string, unknown> | undefined {
  if (!cf || !removed.length) {
    return undefined;
  }

  const next = { ...cf };
  let changed = false;

  // ===== 分别处理条形图条件、数值条件两个子列表 =====
  for (const listKey of CONDITIONAL_FORMAT_LIST_KEYS) {
    const items = next[listKey] as TFormatRow[] | undefined;
    if (!Array.isArray(items)) {
      // 未配置该子列表，跳过
      continue;
    }
    const filtered = items.filter(item => !isRowRemoved(item, removed));
    if (filtered.length !== items.length) {
      next[listKey] = filtered;
      changed = true;
    }
  }

  // 无行被删则不产出 patch，避免 pipeline 无意义写回
  return changed ? next : undefined;
}

/**
 * 字段身份改名后，迁移 conditional_format 内对列名的引用。
 *
 * - 始终尝试更新 `metric`
 * - 仅当 `id` 经 mapName 后发生变化时才改 `id`（QB 稳定 uuid 通常不会被 mapName 命中）
 *
 * @param cf - 单层配置（base 或 draft 上的 conditional_format 对象）
 * @param renames - QB 字段身份改名事件（调用方应已过滤 displayOnly）
 * @returns 有字段被迁移时返回浅拷贝后的新对象；否则 `undefined`
 */
function migrateConditionalFormat(
  cf: Record<string, unknown> | undefined,
  renames: IQBRenameEvent[],
): Record<string, unknown> | undefined {
  if (!cf || !renames.length) {
    return undefined;
  }

  /**
   * 将旧 column_name 映射为新 column_name；未命中改名表则原样返回。
   *
   * @param name - 待映射的列名或历史 id 字符串
   * @returns 新列名，或入参本身（无对应 rename 时）
   */
  const mapName = (name: string): string => {
    const hit = renames.find(r => r.prevColumnName === name);
    return hit ? hit.nextColumnName : name;
  };

  /**
   * 迁移单个条件格式子列表（table_bar_percentage 或 table_numerical_values）。
   *
   * @param items - 子列表数组
   * @returns 有行变更时返回新数组；否则 `undefined`
   */
  const migrateList = (
    items: TFormatRow[] | undefined,
  ): TFormatRow[] | undefined => {
    if (!items?.length) {
      return undefined;
    }

    let changed = false;
    const next = items.map(item => {
      let rowChanged = false;
      const row: TFormatRow = { ...item };

      // ===== 同步 metric（与表格列 originName 对齐的主键） =====
      if (row.metric) {
        const mapped = mapName(row.metric);
        if (mapped !== row.metric) {
          row.metric = mapped;
          rowChanged = true;
        }
      }

      // ===== 历史数据：id 可能等于旧 column_name，需跟随身份改名 =====
      if (row.id) {
        const mappedId = mapName(String(row.id));
        if (mappedId !== row.id) {
          row.id = mappedId;
          rowChanged = true;
        }
      }

      if (rowChanged) {
        changed = true;
        return row;
      }
      // 本行无改名命中，保持原引用，减少无意义的对象分配
      return item;
    });

    return changed ? next : undefined;
  };

  const next = { ...cf };
  let layerChanged = false;

  // ===== 两个子列表独立迁移，任一变更即标记 layerChanged =====
  for (const listKey of CONDITIONAL_FORMAT_LIST_KEYS) {
    const migrated = migrateList(next[listKey] as TFormatRow[] | undefined);
    if (migrated) {
      next[listKey] = migrated;
      layerChanged = true;
    }
  }

  return layerChanged ? next : undefined;
}

/**
 * QB 删除指标或明细 raw 列时，移除条件格式中指向该字段的配置行。
 *
 * subscribe: metricRemoved | rawColumnRemoved
 * phase: prune
 */
export const conditionalFormatHandler: IQBLinkageHandler = {
  id: 'conditionalFormat',
  phase: 'prune',
  keys: ['conditional_format'],
  subscribe: ['metricRemoved', 'rawColumnRemoved'],
  apply(changeSet, ctx) {
    // 任一层未配置过 conditional_format 则跳过，避免写入空对象
    if (!hasNonEmptyRecordInLayers(ctx.base, ctx.draft, 'conditional_format')) {
      return;
    }

    /*
     * 明细 / 聚合 prune 数据源分离（与 switchChartCalculateMode 保留 columnMetrics 的设计一致）：
     * - raw：条件格式引用的是明细操作区 columnRawColumns；聚合槽位清空会产生 metricRemoved 事件，
     *   但不应裁剪条件格式（否则 Confirm 后易误删仍挂在 raw 列上的配置）。
     * - aggregation：沿用 metricLikeRemovedFields（removedMetrics + removedRawColumns）。
     */
    const isRawMode = ctx.qb.mode === 'raw';
    const removedSource = isRawMode
      ? changeSet.removedRawColumns ?? []
      : metricLikeRemovedFields(changeSet);
    const removed = removedSource.map(m => ({
      column_name: m.column_name,
      table_code: m.table_code || 't0',
    }));
    if (!removed.length) {
      return;
    }

    // ===== 分别裁剪 base / draft，再合并为 pipeline patch =====
    const baseNext = pruneConditionalFormat(
      ctx.base.conditional_format as Record<string, unknown> | undefined,
      removed,
    );
    const draftNext = pruneConditionalFormat(
      ctx.draft.conditional_format as Record<string, unknown> | undefined,
      removed,
    );

    return patchBoth(
      baseNext ? { conditional_format: baseNext } : undefined,
      draftNext ? { conditional_format: draftNext } : undefined,
    );
  },
};

/**
 * QB 字段 column_name 身份改名（非仅展示名）时，同步更新条件格式中的 metric / 历史 id。
 *
 * subscribe: fieldIdentityRenamed
 * phase: migrate
 */
export const conditionalFormatMigrateHandler: IQBLinkageHandler = {
  id: 'conditionalFormat-migrate',
  phase: 'migrate',
  keys: ['conditional_format'],
  subscribe: ['fieldIdentityRenamed'],
  apply(changeSet, ctx) {
    if (!hasNonEmptyRecordInLayers(ctx.base, ctx.draft, 'conditional_format')) {
      return;
    }

    // displayOnly 仅改展示名，column_name 不变，conditional_format 中的 metric 无需迁移
    const renames = changeSet.renamed.filter(r => !r.displayOnly);
    if (!renames.length) {
      return;
    }

    const baseNext = migrateConditionalFormat(
      ctx.base.conditional_format as Record<string, unknown> | undefined,
      renames,
    );
    const draftNext = migrateConditionalFormat(
      ctx.draft.conditional_format as Record<string, unknown> | undefined,
      renames,
    );

    return patchBoth(
      baseNext ? { conditional_format: baseNext } : undefined,
      draftNext ? { conditional_format: draftNext } : undefined,
    );
  },
};
